import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useInvalidate, useTranslate } from "@refinedev/core";
import { AlertTriangle, Pill } from "lucide-react";
import { DateField, Button } from "@fawkes/ui";

import { Werklijst, WeergaveKeuze, useWeergave, type Kolom, type Rij } from "../components/WerklijstWeergave";
import { PensionLijstFilters, PensionLijstKop, PensionLijstMeta, PensionLijstTegel, zoekIn } from "../components/PensionLijstKop";
import { todayIso } from "../i18n/format";
import "./PensionWerklijst.css";

type Staat = "GEPLAND" | "ACHTERSTALLIG" | "GEGEVEN";

interface Regel {
  dierId: number;
  dierNaam: string | null;
  diersoort: string | null;
  klantId: number | null;
  klantNaam: string | null;
  kennel: string | null;
  medicijnId: string;
  toedieningId: string;
  medicijn: string | null;
  dosering: string | null;
  toedieningsvorm: string | null;
  tijd: string | null;
  door: string | null;
  opmerking: string | null;
  staat: Staat;
}

interface MedicatieData {
  vestigingId: number;
  datum: string;
  telling: { teGeven: number; gegeven: number; achterstallig: number; dieren: number };
  regels: Regel[];
}

/** De soortfilters. "Overig" vangt alles wat geen hond of kat is, zodat er nooit iets wegvalt. */
type Soort = "ALLE" | "HOND" | "KAT" | "OVERIG";

function soortVan(diersoort: string | null): Exclude<Soort, "ALLE"> {
  const s = (diersoort ?? "").toLowerCase();
  if (s.startsWith("hond")) return "HOND";
  if (s.startsWith("kat") || s.startsWith("poes")) return "KAT";
  return "OVERIG";
}

/**
 * Het overzicht "Medicatie" — wens §9 uit de wensenlijst.
 *
 * <p>Eén regel per <b>moment</b> en niet per dier: een hond met drie middelen op drie tijden is drie
 * keer werk, en dat is wat er op een werklijst hoort te staan. Afvinken gaat door naar het bestaande
 * aftekenen van het dierdossier — inclusief de logboekregel met de naam van het middel erin.
 *
 * <p>Wat er nieuw bij kwam zijn de <b>initialen</b>: die stonden er niet, terwijl de wens er expliciet
 * om vraagt. Ze staan náást het ingelogde account, niet in plaats daarvan — aan de balie blijft één
 * computer ingelogd, dus zonder initialen zegt de lijst straks bij elke regel dezelfde naam.
 */
export function PensionMedicatie({ onOpenDier }: { onOpenDier?: (dierId: number) => void }) {
  const t = useTranslate();
  const invalidate = useInvalidate();
  const [datum, setDatum] = useState(() => todayIso());
  const [soort, setSoort] = useState<Soort>("ALLE");
  const [zoek, setZoek] = useState("");
  const [weergave, setWeergave] = useWeergave("medicatie");
  const [initialen, setInitialen] = useState("");
  const [fout, setFout] = useState<string | null>(null);

  const query = useMemo(() => ({ datum }), [datum]);
  const { data, isLoading, isError, refetch } = useCustom<MedicatieData>({
    url: "pension/medicatie",
    method: "get",
    config: { query },
  });
  const { mutate, isLoading: bezig } = useCustomMutation();

  const medicatie = data?.data;
  const alle = medicatie?.regels ?? [];
  const regels = zoekIn(soort === "ALLE" ? alle : alle.filter((r) => soortVan(r.diersoort) === soort), zoek);

  const aantalPerSoort = (s: Soort) =>
    s === "ALLE" ? alle.length : alle.filter((r) => soortVan(r.diersoort) === s).length;

  function tekenAf(regel: Regel) {
    setFout(null);
    mutate(
      {
        url: "pension/medicatie",
        method: "post",
        values: {
          dierId: regel.dierId,
          medicijnId: regel.medicijnId,
          toedieningId: regel.toedieningId,
          initialen: initialen.trim() || null,
        },
      },
      {
        onSuccess: () => {
          refetch();
          // Het dierdossier toont dezelfde toedieningen; anders staat daar straks een oude stand.
          invalidate({ resource: "animals", invalidates: ["all"] });
        },
        onError: () => setFout(regel.toedieningId),
      },
    );
  }

  // Tabel en tegels komen uit dezelfde kolommen (WerklijstWeergave).
  const kolommen: Kolom<(typeof regels)[number]>[] = [
    {
      kop: t("pensionMedicatie.kolom.tijd"),
      cel: (r) => <>{r.tijd ?? "—"}</>,
      klasse: "pension-werklijst__getal",
    },
    {
      kop: t("pensionMedicatie.kolom.dier"),
      cel: (r) => (
        <>
          {onOpenDier ? (
            <button
              type="button"
              className="pension-werklijst__link"
              onClick={() => onOpenDier(r.dierId)}
            >
              {r.dierNaam ?? "—"}
            </button>
          ) : (
            (r.dierNaam ?? "—")
          )}
          {r.diersoort && <span className="pension-chip">{r.diersoort}</span>}
        </>
      ),
      rol: "dier",
    },
    {
      kop: t("pensionMedicatie.kolom.kennel"),
      cel: (r) => <>{r.kennel ?? "—"}</>,
      rol: "verblijf",
    },
    {
      kop: t("pensionMedicatie.kolom.medicijn"),
      cel: (r) => (
        <>
          {r.medicijn ?? "—"}
          {r.toedieningsvorm && (
            <span className="pension-werklijst__door"> · {r.toedieningsvorm}</span>
          )}
        </>
      ),
    },
    {
      kop: t("pensionMedicatie.kolom.dosering"),
      cel: (r) => <>{r.dosering ?? "—"}</>,
    },
    {
      kop: t("pensionMedicatie.kolom.staat"),
      cel: (r) => (
        <>
          <span className="pension-werklijst__reden" data-reden={r.staat}>
            {r.staat === "ACHTERSTALLIG" && <AlertTriangle size={13} aria-hidden="true" />}
            {t(`pensionMedicatie.staat.${r.staat}`)}
          </span>
          {r.staat === "GEGEVEN" && r.door && (
            <span className="pension-werklijst__door"> · {r.door}</span>
          )}
        </>
      ),
    },
    {
      kop: t("pensionMedicatie.kolom.afvinken"),
      cel: (r) => (
        <>
          <span className="pension-werklijst__invoer">
            {r.staat === "GEGEVEN" ? (
              <span className="fawkes-muted">{t("pensionMedicatie.klaar")}</span>
            ) : (
              <Button variant="primary" disabled={bezig} onClick={() => tekenAf(r)}>
                {t("pensionMedicatie.gegeven")}
              </Button>
            )}
            {fout === r.toedieningId && (
              <span className="fawkes-error" role="status">
                {t("pensionMedicatie.mislukt")}
              </span>
            )}
          </span>
        </>
      ),
      rol: "actie",
    },
  ];
  const rij = (r: (typeof regels)[number]): Rij => ({ key: r.toedieningId, "data-signaal": r.staat === "ACHTERSTALLIG" ? "ACTIE_NODIG" : "OK", "data-tewegen": r.staat === "GEGEVEN" ? undefined : "" });

  return (
    <div className="pension-werklijst fawkes-pgo">
      <PensionLijstKop icoon={<Pill size={22} />} titel={t("pensionMedicatie.title")} subtitel={t("pensionMedicatie.subtitle")} soort={{ opties: (["HOND", "KAT", "OVERIG"] as Soort[]).map((s) => ({ sleutel: s, label: t(`pensionMedicatie.soort.${s}`), aantal: aantalPerSoort(s) })), actief: soort, onKies: (s) => setSoort(s as Soort), totaal: aantalPerSoort("ALLE") }}>
          <PensionLijstTegel waarde={medicatie?.telling.teGeven} label={t("pensionMedicatie.teller.teGeven")} />
          <PensionLijstTegel waarde={medicatie?.telling.gegeven} label={t("pensionMedicatie.teller.gegeven")} />
          <PensionLijstTegel
            waarde={medicatie?.telling.achterstallig}
            label={t("pensionMedicatie.teller.achterstallig")}
            toon={medicatie && medicatie.telling.achterstallig > 0 ? "rood" : undefined}
          />
          <PensionLijstTegel waarde={medicatie?.telling.dieren} label={t("pensionMedicatie.teller.dieren")} />
      </PensionLijstKop>

      <PensionLijstFilters
        zoek={zoek}
        onZoek={setZoek}
        weergave={<WeergaveKeuze waarde={weergave} onKies={setWeergave} />}
        rechts={
          <div className="pension-werklijst__balk">
            <input
              type="text"
              className="pension-werklijst__initialen"
              value={initialen}
              maxLength={10}
              placeholder={t("pensionMedicatie.initialen")}
              aria-label={t("pensionMedicatie.initialen")}
              onChange={(e) => setInitialen(e.target.value)}
            />
            <label className="po-veld werklijst-datum">
              <span>{t("pensionOverzichten.datum")}</span>
              <DateField className="pension-werklijst__datum"
                value={datum}
                onChange={(e) => e.target.value && setDatum(e.target.value)}
                aria-label={t("pensionMedicatie.datum")}
              />
            </label>
            <Button variant="secondary" onClick={() => setDatum(todayIso())}>
              {t("pensionMedicatie.vandaag")}
            </Button>
          </div>
        }
      />

      {isError ? (
        <p className="fawkes-muted">{t("pensionMedicatie.loadFailed")}</p>
      ) : (
        <>
          <PensionLijstMeta aantal={regels.length} />
          {isLoading ? (
            <p className="pension-werklijst__leeg">{t("common.loading")}</p>
          ) : regels.length === 0 ? (
            <p className="pension-werklijst__leeg">
              <Pill size={26} aria-hidden="true" />
              {t("pensionMedicatie.leeg")}
            </p>
          ) : (
            <Werklijst lijst="medicatie" regels={regels} kolommen={kolommen} weergave={weergave} rij={rij} dierId={(r) => r.dierId} />
          )}
        </>
      )}
    </div>
  );
}

